import { useState, type ReactNode } from 'react'
import { noteMeta, sortNotes } from '@/lib/note-meta'
import { ManageStagesLink } from '@/components/shared/manage-links'
import { ContactHeader } from './contact-detail-header'
import { ConnectionDataCard } from './connection-data-card'
import type { TimelineItem } from '@/hooks/use-contact-overview'
import {
  BehaviorSummaryCard,
  LinkedSourcesCard,
  OrderSourceFilter,
  ChannelsCard,
  MemberSegmentsCard,
  ProfileCard,
  RecentActivityCard,
  RecentOrdersCard,
  RfmCard,
  SectionCard,
  TopProductsCard,
  Timeline,
  TrendChartCard,
} from './contact-detail-sections'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { StageBadge } from '@/components/shared/stage-badge'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Activity,
  ArrowLeft,
  Brain,
  CalendarClock,
  Sparkles,
  Trash2,
  Pin,
  PinOff,
  Plus,
  LayoutDashboard,
  NotebookPen,
  ShoppingBag,
  UserRound,
} from 'lucide-react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EvotechMemberSection, OrdersSection } from '@/pages/conversations/contact-commerce'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useContactProperties, type ContactPropertyValue } from '@/hooks/use-cdp'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_TYPE_LABELS,
  appointmentStatusVariant,
  INTENT_LABELS,
  intentBadgeVariant,
  LIFECYCLE_STAGES,
  SENTIMENT_LABELS,
  sentimentBadgeVariant,
  stageLabel,
  useAppointments,
  useContact,
  useCreateAppointment,
  useCreateNote,
  useDeleteAppointment,
  useDeleteContact,
  useDeleteNote,
  useNotes,
  useToggleNotePin,
  useUpdateAppointment,
  useUpdateContact,
  type Appointment,
  type ContactDetail,
  type ContactUpdateInput,
  type Note,
} from '@/hooks/use-contacts'

const DETAIL_TABS = [
  { value: 'overview', get label() { return i18n.t('custDetail:tabs.overview') }, icon: LayoutDashboard },
  { value: 'profile', get label() { return i18n.t('custDetail:tabs.profile') }, icon: UserRound },
  { value: 'purchases', get label() { return i18n.t('custDetail:tabs.purchases') }, icon: ShoppingBag },
  { value: 'activity', get label() { return i18n.t('custDetail:tabs.activity') }, icon: Activity },
  { value: 'notes', get label() { return i18n.t('custDetail:tabs.notes') }, icon: NotebookPen },
] as const
export type DetailTab = (typeof DETAIL_TABS)[number]['value']

/** Link cũ (?tab=orders|behavior|interactions|history|segments) vẫn mở đúng tab sau khi gộp. */
const LEGACY_TAB: Record<string, DetailTab> = {
  orders: 'purchases',
  behavior: 'activity',
  interactions: 'activity',
  history: 'activity',
  segments: 'profile',
}

export function resolveDetailTab(raw: string | null | undefined): DetailTab {
  const r = raw ?? ''
  return DETAIL_TABS.some((t) => t.value === r) ? (r as DetailTab) : (LEGACY_TAB[r] ?? 'overview')
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = resolveDetailTab(searchParams.get('tab'))
  const goTab = (t: string) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true })
  return <ContactDetailView id={id} tab={tab} onTab={goTab} onDeleted={() => navigate('/customers')} top={<BackLink />} />
}

/**
 * Nội dung chi tiết khách — dùng chung cho trang /customers/:id và ngăn trượt phải ở màn Hội thoại (contact-detail-drawer.tsx).
 * `compact` = đặt trong ngăn (hẹp hơn trang): bố cục 3 cột chỉ bật ở màn rất rộng.
 */
export function ContactDetailView({ id, tab, onTab, onDeleted, top, compact = false }: {
  id: string | undefined
  tab: DetailTab
  onTab: (t: string) => void
  onDeleted: () => void
  top?: ReactNode
  compact?: boolean
}) {
  const { t } = useTranslation('custDetail')
  const { data, isLoading, isError } = useContact(id)
  // Sửa hồ sơ NGAY TRÊN trang (thẻ Hồ sơ khách hàng chuyển thành ô điền) — không còn hộp thoại.
  const [profileEditing, setProfileEditing] = useState(false)
  const [orderSource, setOrderSource] = useState('all')
  const src = orderSource === 'all' ? undefined : orderSource
  const deleteContact = useDeleteContact()
  useLifecycleStages() // nạp nhãn giai đoạn của doanh nghiệp cho stageLabel()
  const goTab = onTab

  if (isLoading) return <Loading label={t('page.loading')} />
  if (isError || !data)
    return (
      <div className="space-y-4">
        {top}
        <ErrorState message={t('page.notFound')} />
      </div>
    )

  // "Chỉnh sửa" từ nút Hành động: sang tab Hồ sơ (thẻ hồ sơ nằm ở đó) rồi mở ô điền.
  const startEdit = () => {
    if (tab !== 'profile' && tab !== 'overview') goTab('profile')
    setProfileEditing(true)
  }

  const onDelete = () => {
    if (!id) return
    if (!confirm(t('page.confirmDelete'))) return
    deleteContact.mutate(id, {
      onSuccess: () => {
        toast.success(t('page.deleted'))
        onDeleted()
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-5">
      {top}
      <ContactHeader contact={data} onEdit={startEdit} onDelete={onDelete} deleting={deleteContact.isPending} onGoTab={goTab} />

      <Tabs value={tab} onValueChange={goTab}>
        <div className="w-full overflow-x-auto rounded-xl border bg-card px-2">
          <TabsList className="h-auto bg-transparent">
            {DETAIL_TABS.map((tb) => (
              <TabsTrigger key={tb.value} value={tb.value} className="gap-2 py-3">
                <tb.icon className="h-4 w-4" /> {tb.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-5">
          <div className={cn('grid grid-cols-1 gap-5', compact ? '2xl:grid-cols-[300px_minmax(0,1fr)_320px]' : 'xl:grid-cols-[320px_minmax(0,1fr)_340px]')}>
            <div className="space-y-5">
              <ProfileCard contact={data} editing={profileEditing} onEdit={startEdit} onClose={() => setProfileEditing(false)} />
              <MemberSegmentsCard contactId={data.id} />
              <RfmCard contactId={data.id} />
            </div>
            <div className="min-w-0 space-y-5">
              <BehaviorSummaryCard contactId={data.id} />
              <TrendChartCard contactId={data.id} />
              <TopProductsCard contactId={data.id} />
              <RecentOrdersCard contactId={data.id} onAll={() => goTab('purchases')} />
            </div>
            <div className="space-y-5">
              <ChannelsCard contact={data} />
              <RecentActivityCard contactId={data.id} stageLabel={stageLabel} onAll={() => goTab('activity')} />
              <AiInsightsCard contact={data} />
              <ContactPropertiesCard contactId={data.id} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-5">
              <ProfileCard contact={data} editing={profileEditing} onEdit={startEdit} onClose={() => setProfileEditing(false)} />
              <ContactPropertiesCard contactId={data.id} />
              <MemberSegmentsCard contactId={data.id} />
            </div>
            <div className="space-y-5">
              <LinkedSourcesCard contactId={data.id} />
              <ConnectionDataCard contactId={data.id} />
              <EvotechMemberSection contactId={data.id} />
              <AiInsightsCard contact={data} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="purchases" className="mt-5 space-y-5">
          <OrderSourceFilter contactId={data.id} value={orderSource} onChange={setOrderSource} />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
            <RfmCard contactId={data.id} source={src} />
            <TrendChartCard contactId={data.id} source={src} />
          </div>
          <TopProductsCard contactId={data.id} source={src} />
          <SectionCard title={t('page.orders')}>
            <OrdersSection contactId={data.id} bare source={src} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="activity" className="mt-5 space-y-5">
          <BehaviorSummaryCard contactId={data.id} />
          <ChannelsCard contact={data} />
          <SectionCard title={t('page.timeline')}>
            <ActivityTimeline contact={data} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="notes" className="mt-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <NotesTab contactId={data.id} />
            <AppointmentsTab contactId={data.id} />
          </div>
        </TabsContent>
      </Tabs>

    </div>
  )
}

function BackLink() {
  const { t } = useTranslation('custDetail')
  return (
    <Link
      to="/customers"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" /> {t('page.backToList')}
    </Link>
  )
}

// ── Thuộc tính khách (dữ liệu dùng để phân khúc) ─────────────────────
function formatPropertyValue(p: ContactPropertyValue): string {
  const raw = p.value?.trim()
  if (!raw) return ''
  const label = (v: string) => p.options.find((o) => o.value === v)?.label ?? v
  if (p.fieldType === 'boolean') return raw === 'true' ? i18n.t('custDetail:props.yes') : i18n.t('custDetail:props.no')
  if (p.fieldType === 'date') return dayjs(raw).isValid() ? dayjs(raw).format('DD/MM/YYYY') : raw
  if (p.fieldType === 'single_select') return label(raw)
  if (p.fieldType === 'multi_select') {
    let items: string[]
    try {
      const parsed = JSON.parse(raw)
      items = Array.isArray(parsed) ? parsed.map(String) : [raw]
    } catch {
      items = raw.split(',').map((x) => x.trim()).filter(Boolean)
    }
    return items.map(label).join(', ')
  }
  return raw
}

function ContactPropertiesCard({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const { data } = useContactProperties(contactId)
  const filled = (data?.properties ?? []).filter((p) => formatPropertyValue(p))
  if (!filled.length) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" /> {t('props.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {filled.map((p) => (
          <div key={p.id}>
            <p className="text-xs text-muted-foreground">{p.name}</p>
            <p className="break-words font-medium">{formatPropertyValue(p)}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// ── AI insights ──────────────────────────────────────────────────────
function AiInsightsCard({ contact }: { contact: ContactDetail }) {
  const { t } = useTranslation('custDetail')
  const hasAi = contact.aiSummary || contact.aiSentimentLabel || contact.aiIntent
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" /> AI insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!hasAi ? (
          <p className="text-muted-foreground">{t('ai.empty')}</p>
        ) : (
          <>
            {contact.aiSummary && <p className="leading-relaxed">{contact.aiSummary}</p>}

            <div className="flex flex-wrap gap-2">
              {contact.aiSentimentLabel && (
                <Badge variant={sentimentBadgeVariant(contact.aiSentimentLabel)}>
                  {t('ai.sentiment', { value: SENTIMENT_LABELS[contact.aiSentimentLabel] ?? contact.aiSentimentLabel })}
                </Badge>
              )}
              {contact.aiIntent && (
                <Badge variant={intentBadgeVariant(contact.aiIntent)}>
                  {t('ai.intent', { value: INTENT_LABELS[contact.aiIntent] ?? contact.aiIntent })}
                </Badge>
              )}
            </div>

            {contact.aiSentimentReason && (
              <p className="text-xs text-muted-foreground">{contact.aiSentimentReason}</p>
            )}

            {contact.aiPainPoints?.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-semibold text-muted-foreground">{t('ai.painPoints')}</p>
                <ul className="list-inside list-disc space-y-1">
                  {contact.aiPainPoints.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}

            {contact.aiAnalyzedAt && (
              <p className="text-xs text-muted-foreground">
                {t('ai.analyzedAt', { time: dayjs(contact.aiAnalyzedAt).format('HH:mm DD/MM/YYYY') })}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Dialog chỉnh sửa ─────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

// ── Tab Ghi chú ──────────────────────────────────────────────────────
function NotesTab({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const { data, isLoading } = useNotes(contactId)
  const create = useCreateNote()
  const togglePin = useToggleNotePin(contactId)
  const del = useDeleteNote(contactId)
  const [content, setContent] = useState('')

  const onAdd = () => {
    if (!content.trim()) return
    create.mutate(
      { contactId, content },
      {
        onSuccess: () => {
          setContent('')
          toast.success(t('notes.added'))
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 pt-6">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('notes.placeholder')}
          />
          <div className="flex justify-end">
            <Button onClick={onAdd} disabled={create.isPending || !content.trim()}>
              <Plus className="h-4 w-4" /> {t('notes.add')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Loading />
      ) : !data?.notes.length ? (
        <EmptyState title={t('notes.emptyTitle')} description={t('notes.emptyDesc')} />
      ) : (
        <div className="space-y-3">
          {sortNotes(data.notes).map((n) => (
            <NoteRow
              key={n.id}
              note={n}
              onPin={() => togglePin.mutate(n.id)}
              onDelete={() => {
                if (confirm(t('notes.confirmDelete')))
                  del.mutate(n.id, {
                    onSuccess: () => toast.success(t('notes.deleted')),
                    onError: (e) => toast.error(apiError(e)),
                  })
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function NoteRow({
  note,
  onPin,
  onDelete,
}: {
  note: Note
  onPin: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('custDetail')
  const meta = noteMeta(note)
  return (
    <Card className={note.isPinned ? 'border-primary/40' : undefined}>
      <CardContent className="flex items-start justify-between gap-3 pt-6">
        <div className="min-w-0 space-y-1">
          <p className="whitespace-pre-wrap break-words text-sm">{note.content}</p>
          <p className="text-xs text-muted-foreground">
            {meta.author} · {meta.when}
          </p>
          {meta.sourceNote && <p className="text-[11px] text-muted-foreground/80">{meta.sourceNote}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="icon" onClick={onPin} title={note.isPinned ? t('common:actions.unpin') : t('common:actions.pin')}>
            {note.isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} title={t('notes.delete')}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Tab Lịch hẹn ─────────────────────────────────────────────────────
function AppointmentsTab({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const { data, isLoading } = useAppointments(contactId)
  const create = useCreateAppointment(contactId)
  const update = useUpdateAppointment(contactId)
  const del = useDeleteAppointment(contactId)

  const [date, setDate] = useState('')
  const [type, setType] = useState('meeting')
  const [notes, setNotes] = useState('')

  const onAdd = () => {
    if (!date) {
      toast.error(t('appointments.pickTime'))
      return
    }
    create.mutate(
      {
        contactId,
        appointmentDate: new Date(date).toISOString(),
        type,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setDate('')
          setNotes('')
          setType('meeting')
          toast.success(t('appointments.created'))
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('appointments.time')}>
              <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label={t('appointments.type')}>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(APPOINTMENT_TYPE_LABELS).map(([v, label]) => (
                    <SelectItem key={v} value={v}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t('appointments.notes')}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('appointments.notesPlaceholder')} />
          </Field>
          <div className="flex justify-end">
            <Button onClick={onAdd} disabled={create.isPending}>
              <Plus className="h-4 w-4" /> {t('appointments.create')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Loading />
      ) : !data?.appointments.length ? (
        <EmptyState title={t('appointments.emptyTitle')} description={t('appointments.emptyDesc')} />
      ) : (
        <div className="space-y-3">
          {data.appointments.map((a) => (
            <AppointmentRow
              key={a.id}
              appt={a}
              onStatus={(status) => update.mutate({ id: a.id, data: { status } })}
              onDelete={() => {
                if (confirm(t('appointments.confirmDelete')))
                  del.mutate(a.id, {
                    onSuccess: () => toast.success(t('appointments.deleted')),
                    onError: (e) => toast.error(apiError(e)),
                  })
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AppointmentRow({
  appt,
  onStatus,
  onDelete,
}: {
  appt: Appointment
  onStatus: (status: string) => void
  onDelete: () => void
}) {
  const { t } = useTranslation('custDetail')
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-3 pt-6">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">
              {dayjs(appt.appointmentDate).format('HH:mm DD/MM/YYYY')}
            </span>
            <Badge variant="outline">{APPOINTMENT_TYPE_LABELS[appt.type] ?? appt.type}</Badge>
            <Badge variant={appointmentStatusVariant(appt.status)}>
              {APPOINTMENT_STATUS_LABELS[appt.status] ?? appt.status}
            </Badge>
          </div>
          {appt.notes && <p className="whitespace-pre-wrap break-words text-sm">{appt.notes}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Select value={appt.status} onValueChange={onStatus}>
            <SelectTrigger className="h-8 w-[150px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(APPOINTMENT_STATUS_LABELS).map(([v, label]) => (
                <SelectItem key={v} value={v}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={onDelete} title={t('notes.delete')}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Hoạt động: hội thoại + sự kiện + đổi giai đoạn + ghi chú trong một dòng thời gian có lọc ──
const ACTIVITY_FILTERS: { value: 'all' | TimelineItem['type']; label: string }[] = [
  { value: 'all', get label() { return i18n.t('custDetail:activity.all') } },
  { value: 'message', get label() { return i18n.t('custDetail:activity.message') } },
  { value: 'event', get label() { return i18n.t('custDetail:activity.event') } },
  { value: 'lifecycle', get label() { return i18n.t('custDetail:activity.lifecycle') } },
  { value: 'note', get label() { return i18n.t('custDetail:activity.note') } },
  { value: 'appointment', get label() { return i18n.t('custDetail:activity.appointment') } },
]

function ActivityTimeline({ contact }: { contact: ContactDetail }) {
  const { t } = useTranslation('custDetail')
  const [f, setF] = useState<(typeof ACTIVITY_FILTERS)[number]['value']>('all')
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('activity.filterAria')}>
        {ACTIVITY_FILTERS.map((x) => (
          <button
            key={x.value}
            type="button"
            aria-pressed={f === x.value}
            onClick={() => setF(x.value)}
            className={`rounded-full border px-3 py-1 text-xs ${f === x.value ? 'border-primary bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-muted'}`}
          >
            {x.label}
          </button>
        ))}
      </div>
      <Timeline contactId={contact.id} limit={100} types={f === 'all' ? undefined : [f]} stageLabel={stageLabel} />
    </div>
  )
}
